import {
  Typography,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Select,
  MenuItem,
  Box,
  Tooltip,
  Switch,
} from "@mui/material";
import { CheckIcon } from "common-lib";
import { Info } from "lucide-react";
import React, { useEffect } from "react";
import { useRecoilState } from "recoil";
import { t } from "i18next";
import { projectCountInfoState } from "../../../recoil";

interface Props {
  isSaved: boolean;
  autoLabeling: boolean;
  thresholdCustom: string | number;
  targetLabel: string;
  minTrainCount: string | number;
  autoLabelingCondition: string;
  conditionList: string[];
  classList: string[];
  onSave: () => void;
  onAutoLabelingChange: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onInputChange: React.ChangeEventHandler<HTMLInputElement>;
  onTargetLabelChange: (v: string) => void;
  onAutoLabelingConditionChange: (v: string) => void;
}

export default function TrainSettingPanel({
  isSaved,
  autoLabeling,
  thresholdCustom,
  targetLabel,
  minTrainCount,
  autoLabelingCondition,
  conditionList,
  classList,
  onSave,
  onAutoLabelingChange,
  onInputChange,
  onTargetLabelChange,
  onAutoLabelingConditionChange,
}: Props) {
  return (
    <Box
      id="auto-train-panel"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 240,
        position: "absolute",
        top: "100%",
        left: 0,
        mt: 0.5,
        borderRadius: 2,
        border: "0.5px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        zIndex: 10,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: "0.5px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{t("AutoLabelingSetting")}</Typography>
        {!isSaved && (
          <IconButton size="small" onClick={onSave} sx={{ p: 0 }}>
            <CheckIcon size={14} color="var(--complete-check)" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("AutoLabeling")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" sx={{ color: autoLabeling ? "text.disabled" : "text.secondary" }}>
              {t("Off")}
            </Typography>
            <Switch checked={autoLabeling} onChange={onAutoLabelingChange} size="small" />
            <Typography
              variant="caption"
              sx={{ color: autoLabeling ? "primary.main" : "text.disabled", fontWeight: autoLabeling ? 500 : 400 }}
            >
              {t("On")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.625 }}>
          <Typography
            sx={{ fontSize: 11, color: "text.disabled", letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            {t("MinCount")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
            <Select
              value={targetLabel}
              onChange={(e) => onTargetLabelChange(e.target.value)}
              size="small"
              sx={{ fontSize: 12, height: 28, "& .MuiSelect-select": { py: 0 } }}
            >
              {classList.map((labelName, index) => (
                <MenuItem key={index} value={labelName} sx={{ fontSize: 12 }}>
                  {labelName}
                </MenuItem>
              ))}
            </Select>
            <TextField
              name="minTrainCount"
              value={minTrainCount}
              size="small"
              onChange={onInputChange}
              onKeyDown={(e) => e.stopPropagation()}
              inputProps={{
                style: {
                  textAlign: "right",
                  fontSize: 12,
                  width: "100%",
                  padding: "0 8px",
                  height: 28,
                  boxSizing: "border-box",
                },
              }}
              sx={{ "& .MuiInputBase-root": { height: 28 } }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.625 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              sx={{ fontSize: 11, color: "text.disabled", letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {t("UpdateCondition")}
            </Typography>
            <Tooltip title={t("AutoLabelingUpdateConditionMsg")}>
              <Info size={13} style={{ color: "var(--color-text-tertiary)" }} />
            </Tooltip>
          </Box>
          <Select
            value={autoLabelingCondition}
            onChange={(e) => onAutoLabelingConditionChange(e.target.value)}
            size="small"
            sx={{ width: "100%", fontSize: 12, height: 28, "& .MuiSelect-select": { py: 0 } }}
          >
            {conditionList.map((option, index) => (
              <MenuItem key={index} value={option} sx={{ fontSize: 12 }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  );
}
